import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Discussion } from "../components/Discussion";
import { EmptyState, Loading } from "../components/StateViews";
import { useDebouncedValue } from "../lib/hooks";
import type { RouterOutputs } from "../trpc";
import { trpc } from "../trpc";

type InboxItem = RouterOutputs["helper"]["inbox"][number];
type Order = "oldest" | "newest";

export function HelperPage() {
  const me = trpc.session.me.useQuery();
  const [query, setQuery] = useState("");
  const [order, setOrder] = useState<Order>("oldest");
  const debounced = useDebouncedValue(query.trim());

  const inbox = trpc.helper.inbox.useQuery(
    { q: debounced || undefined, order },
    { enabled: me.data?.role === "helper" || me.data?.role === "admin" },
  );

  const groups = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        dohaSlug: string | null;
        mandal: number | null;
        chapter: number | null;
        doha: number | null;
        items: InboxItem[];
      }
    >();
    for (const it of inbox.data ?? []) {
      const key = it.dohaSlug ?? "__untagged__";
      const g = map.get(key);
      if (g) g.items.push(it);
      else
        map.set(key, {
          key,
          dohaSlug: it.dohaSlug,
          mandal: it.mandal,
          chapter: it.chapter,
          doha: it.doha,
          items: [it],
        });
    }
    return Array.from(map.values());
  }, [inbox.data]);

  if (me.isLoading) return <Loading label="Checking your access" />;

  const role = me.data?.role;
  if (role !== "helper" && role !== "admin") {
    return (
      <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
        <EmptyState title="This area is for translators.">
          <Link to="/" className="text-terracotta hover:underline">
            Return home
          </Link>
        </EmptyState>
      </div>
    );
  }

  const total = inbox.data?.length ?? 0;

  return (
    <div className="mx-auto max-w-4xl px-5 pt-14 sm:px-8">
      <p className="eyebrow">Translator inbox</p>
      <h1 className="mt-3 font-serif text-4xl text-ink">
        Words waiting for meaning
      </h1>
      <p className="mt-3 max-w-2xl leading-relaxed text-ink-soft">
        Each request is a word a reader marked as needing a definition. Answer
        it and the meaning becomes a real glossary sense.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a word or doha — e.g. Vivek, M2C5D2"
          aria-label="Search requests"
          className="w-72 rounded-xl border border-line-strong bg-paper-raised px-4 py-2 text-ink outline-none focus:border-terracotta/60"
        />
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setOrder("oldest")}
            className={pillClass(order === "oldest")}
          >
            Oldest first
          </button>
          <button
            type="button"
            onClick={() => setOrder("newest")}
            className={pillClass(order === "newest")}
          >
            Newest first
          </button>
        </div>
      </div>

      {inbox.isLoading ? (
        <Loading label="Gathering requests" />
      ) : total === 0 ? (
        <div className="mt-10">
          <EmptyState
            title={
              debounced
                ? `Nothing matches “${debounced}”.`
                : "The inbox is clear — no words are waiting."
            }
          />
        </div>
      ) : (
        <div className="mt-10 space-y-10">
          {groups.map((g) => (
            <section key={g.key}>
              <div className="flex items-baseline justify-between gap-3 border-b border-line pb-2">
                <h2 className="font-serif text-2xl text-ink">
                  {g.dohaSlug
                    ? `Mandal ${g.mandal} · Chapter ${g.chapter} · Doha ${g.doha}`
                    : "Untagged captures"}
                </h2>
                {g.dohaSlug ? (
                  <Link
                    to={`/doha/${g.dohaSlug}`}
                    className="font-mono text-sm text-ink-faint transition-colors hover:text-terracotta"
                  >
                    {g.dohaSlug}
                  </Link>
                ) : null}
              </div>
              <ul className="mt-4 space-y-5">
                {g.items.map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function pillClass(active: boolean): string {
  return [
    "rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
    active
      ? "bg-ink text-paper"
      : "border border-line-strong text-ink-faint hover:bg-paper-sunk hover:text-ink",
  ].join(" ");
}

function RequestCard({ request }: { request: InboxItem }) {
  const utils = trpc.useUtils();
  const [definition, setDefinition] = useState("");
  const [resolve, setResolve] = useState(true);

  // Optional: attach the meaning to a DIFFERENT existing glossary word
  // (default is to use the flagged word). A picker, not a raw slug.
  const [termSlug, setTermSlug] = useState("");
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);
  const [wordQuery, setWordQuery] = useState("");
  const debWord = useDebouncedValue(wordQuery.trim());
  const wordResults = trpc.glossary.search.useQuery(
    { q: debWord, limit: 6 },
    { enabled: debWord.length > 0 },
  );

  const answer = trpc.helper.answer.useMutation({
    onSuccess: () => utils.helper.inbox.invalidate(),
  });

  const canSubmit = definition.trim().length > 0 && !answer.isPending;

  return (
    <li className="rounded-2xl border border-line bg-paper-raised p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-serif text-2xl text-ink">{request.label}</h3>
        {request.askedByEmail ? (
          <p className="text-sm text-ink-faint">
            Asked by {request.askedByEmail}
          </p>
        ) : null}
      </div>

      <form
        className="mt-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          answer.mutate({
            annotationId: request.id,
            definition: definition.trim(),
            termSlug: termSlug.trim() || undefined,
            dohaSlug: request.dohaSlug ?? undefined,
            resolve,
          });
        }}
      >
        <div>
          <label className="eyebrow" htmlFor={`definition-${request.id}`}>
            Definition
          </label>
          <textarea
            id={`definition-${request.id}`}
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            rows={3}
            required
            placeholder="Write what this word means…"
            className="mt-2 w-full rounded-xl border border-line-strong bg-paper-raised px-4 py-3 text-ink outline-none focus:border-terracotta/60"
          />
        </div>

        <div>
          <label className="eyebrow">Which glossary word?</label>
          <p className="mt-1 text-xs leading-relaxed text-ink-faint">
            By default this meaning attaches to the flagged word,{" "}
            <span className="text-ink-soft">“{request.label}”</span>. Only pick
            another word if this meaning really belongs to a different existing
            entry.
          </p>

          {termSlug ? (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-terracotta/50 bg-terracotta-wash px-3 py-1.5">
              <span className="font-serif text-sm text-terracotta">
                {pickedLabel ?? termSlug}
              </span>
              <button
                type="button"
                onClick={() => {
                  setTermSlug("");
                  setPickedLabel(null);
                }}
                aria-label="Use the flagged word instead"
                className="text-terracotta/70 transition-colors hover:text-terracotta"
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="relative mt-2">
              <input
                type="search"
                value={wordQuery}
                onChange={(e) => setWordQuery(e.target.value)}
                placeholder={`Search for a different word (or leave blank for “${request.label}”)`}
                className="w-full rounded-xl border border-line-strong bg-paper-raised px-4 py-2 text-ink outline-none focus:border-terracotta/60"
              />
              {debWord.length > 0 &&
              wordResults.data &&
              wordResults.data.length > 0 ? (
                <ul className="absolute z-10 mt-1.5 max-h-56 w-full overflow-auto rounded-xl border border-line bg-paper-raised shadow-md">
                  {wordResults.data.map((t) => (
                    <li key={t.slug}>
                      <button
                        type="button"
                        onClick={() => {
                          setTermSlug(t.slug);
                          setPickedLabel(t.displayName);
                          setWordQuery("");
                        }}
                        className="block w-full px-4 py-2 text-left font-serif text-ink transition-colors hover:bg-paper-sunk"
                      >
                        {t.displayName}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={resolve}
            onChange={(e) => setResolve(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong text-terracotta focus:ring-terracotta/60"
          />
          Resolve this request
        </label>

        <div className="flex items-center justify-between gap-3">
          {answer.error ? (
            <span className="text-sm text-terracotta">
              Couldn’t save — try again.
            </span>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {answer.isPending ? "Saving…" : "Answer & resolve"}
          </button>
        </div>
      </form>

      <div className="mt-6 border-t border-line pt-5">
        <Discussion subjectType="annotation" subjectId={request.id} />
      </div>
    </li>
  );
}
